/*
 * dp-consent.js
 *
 * Connects Shopify's native privacy banner to PostHog's opt-in/opt-out API.
 *
 * PostHog is initialised in `layout/theme.liquid` with `cookieless_mode: 'on_reject'`,
 * which means it stores nothing on the device until it is explicitly opted in. This file
 * is what does the opting, driven entirely by Shopify's own consent state — the store
 * uses Shopify's built-in banner (`storefront-banner.js`), not a third-party CMP, so
 * `Shopify.customerPrivacy` is the single source of truth for what the visitor agreed to.
 *
 * Deferred on purpose. The PostHog stub in the head queues any call made before the real
 * library arrives, so opting in from here is never a race — the queued calls replay in
 * order once `array.js` loads.
 *
 * The `consent-tracking-api` feature is not present on the page by default; it has to be
 * requested. Microsoft Clarity's app-embed block requests it too, and `loadFeatures` is
 * idempotent, so both asking is harmless.
 *
 * It is also the theme's ONE consent reader. `window.dpConsent.ready()` (the same promise
 * on every call) resolves `{ analytics }` once consent is decided — at load when the
 * visitor is outside a banner region or has already answered (`currentVisitorConsent()`
 * — `shouldShowBanner()` is region-only and stays true after an answer), else on the
 * first `visitorConsentCollected`; with no privacy API, `{ analytics: false }` — and
 * always AFTER PostHog has been opted in or
 * out, so the capture popup's first event cannot beat PostHog's consent state. It never
 * rejects: an unanswered banner leaves it pending, and nothing waiting opens over it.
 */
(function () {
  'use strict';

  // Settles once: a later change of mind reaches PostHog, not this.
  var decide;
  var decided = new Promise(function (resolve) {
    decide = function (analyticsAllowed) {
      resolve({ analytics: Boolean(analyticsAllowed) });
    };
  });

  function applyConsent(analyticsAllowed) {
    if (!window.posthog) return;

    if (analyticsAllowed) {
      window.posthog.opt_in_capturing();
    } else {
      // Also clears anything already stored, rather than only stopping new writes.
      window.posthog.opt_out_capturing();
    }
  }

  /*
   * Undecided = in a banner region AND no answer yet. `shouldShowBanner()` alone is
   * region-only: live, it stays true after the visitor has accepted (seen 2026-09-14), so
   * on its own it waited for a `visitorConsentCollected` that never comes to a returning
   * visitor. `currentVisitorConsent().analytics` is '' until they answer ('yes' / 'no'
   * after). Without that method, the region answer is all there is.
   */
  function bannerPending(privacy) {
    if (typeof privacy.shouldShowBanner !== 'function' || !privacy.shouldShowBanner()) return false;
    if (typeof privacy.currentVisitorConsent !== 'function') return true;
    var consent = privacy.currentVisitorConsent();
    return !consent || consent.analytics === '';
  }

  function readCurrentConsent() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return decide(false);

    applyConsent(privacy.analyticsProcessingAllowed());
    if (!bannerPending(privacy)) decide(privacy.analyticsProcessingAllowed());
  }

  /*
   * Fires whenever the visitor answers the banner, including on a later change of mind
   * through the footer's privacy link. Listening for it is what makes a withdrawal of
   * consent take effect without a reload.
   */
  document.addEventListener('visitorConsentCollected', function (event) {
    if (!event || !event.detail) return;
    applyConsent(Boolean(event.detail.analyticsAllowed));
    decide(event.detail.analyticsAllowed);
  });

  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        // PostHog stays untouched here, as it always has; only the waiters are told no.
        if (error) return decide(false);
        readCurrentConsent();
      }
    );
  } else {
    // No Shopify privacy API on the page at all — stay opted out rather than assume consent.
    applyConsent(false);
    decide(false);
  }

  window.dpConsent = {
    ready: function () {
      return decided;
    },
  };
})();
